<template>
  <a
    class="repo-link"
    :class="compact ? 'resource-action' : 'repo-link-row'"
    :href="href"
    target="_blank"
    rel="noopener"
  >
    <img v-if="icon && iconSrc" :src="iconSrc" :alt="iconAlt" class="repo-link-icon repo-link-image">
    <i v-else-if="icon" :class="[iconClass, 'repo-link-icon']" aria-hidden="true"></i>
    <span class="repo-link-text">{{ label }}</span>
  </a>
</template>

<script>
import '../../assets/css/resource-action.css';
export default {
  name: 'GitHubRepoLink',
  props: {
    compact: { type: Boolean, default: true },
    href: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      default: 'View repository',
    },
    icon: {
      type: Boolean,
      default: true,
    },
    iconClass: {
      type: String,
      default: 'fab fa-github',
    },
    iconSrc: {
      type: String,
      default: '',
    },
    iconAlt: {
      type: String,
      default: '',
    },
  },
};
</script>

<style scoped>
/* A quiet, full-width repository row. The control spans the entry's content
   width, stays flat, and on hover swaps to a plain tinted surface so the
   interaction reads as a simple row you can press anywhere. */
.repo-link-row {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  margin-top: 0.95rem;
  padding: 0.6rem 0.85rem;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.55);
  box-shadow: inset 0 0 0 0.5px rgba(0, 0, 0, 0.08);
  color: #5d5d62;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.8rem;
  font-weight: 500;
  line-height: 1.2;
  text-decoration: none;
  transition:
    background-color 160ms ease,
    color 180ms ease,
    box-shadow 160ms ease;
}

.repo-link-icon {
  font-size: 0.95rem;
  flex: 0 0 auto;
  opacity: 0.72;
  transition: opacity 160ms ease;
}

.repo-link-image {
  width: 1rem;
  height: 1rem;
  object-fit: contain;
}

.repo-link-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (hover: hover) and (pointer: fine) {
  .repo-link-row:hover {
    background: rgba(0, 0, 0, 0.035);
    box-shadow: inset 0 0 0 0.5px rgba(0, 0, 0, 0.1);
    color: #2c2c2e;
  }
  .repo-link-row:hover .repo-link-icon {
    opacity: 1;
  }
}

.repo-link-row:active {
  background: rgba(0, 0, 0, 0.055);
  transition-duration: 80ms;
}

.repo-link-row:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 3px;
}

body.dark-mode .repo-link-row {
  background: rgba(255, 255, 255, 0.04);
  box-shadow: inset 0 0 0 0.5px rgba(255, 255, 255, 0.12);
  color: #b9b9be;
}

@media (hover: hover) and (pointer: fine) {
  body.dark-mode .repo-link-row:hover {
    background: rgba(255, 255, 255, 0.065);
    box-shadow: inset 0 0 0 0.5px rgba(255, 255, 255, 0.15);
    color: #e0e0e3;
  }
  body.dark-mode .repo-link-row:active {
    background: rgba(255, 255, 255, 0.085);
  }
}

/* Reduced transparency: the row becomes a solid surface so the label stays
   legible. */
@media (prefers-reduced-transparency: reduce) {
  .repo-link-row {
    background: rgba(248, 248, 250, 0.96);
  }
  body.dark-mode .repo-link-row {
    background: rgba(50, 50, 54, 0.98);
  }
}

@media (prefers-reduced-motion: reduce) {
  .repo-link,
  .repo-link-row .repo-link-icon {
    transition: color 120ms ease, background-color 120ms ease;
  }
}
</style>
